import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import type { Cliente } from '@/types/database'
import {
  useClientes, useCrearCliente, useEditarCliente, useToggleActivoCliente,
  type ClienteFiltros,
} from './hooks'
import {
  PlusIcon, EditIcon, EyeIcon, EyeOffIcon, CloseIcon, SearchIcon,
} from '@/components/shared/Icons'

const fmt = (n: number) =>
  new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ClientesPage() {
  const navigate = useNavigate()
  const [busqueda, setBusqueda]       = useState('')
  const [filtroTipo, setFiltroTipo]   = useState<string>('all')
  const [soloActivos, setSoloActivos] = useState(true)
  const [editando, setEditando]       = useState<Cliente | null>(null)
  const [creando, setCreando]         = useState(false)

  const filtros: ClienteFiltros = {
    busqueda: busqueda || undefined,
    tipo: filtroTipo === 'all' ? undefined : filtroTipo as ClienteFiltros['tipo'],
    soloActivos,
  }
  const { data: clientes = [], isLoading } = useClientes(filtros)
  const toggle = useToggleActivoCliente()

  const conDeuda    = clientes.filter(c => c.saldo_actual > 0).length
  const mayoristas  = clientes.filter(c => c.tipo === 'mayorista').length

  async function handleToggle(c: Cliente) {
    try {
      await toggle.mutateAsync({ id: c.id, activo: !c.activo })
      toast.success(c.activo ? 'Cliente desactivado' : 'Cliente activado')
    } catch {
      toast.error('Error al actualizar cliente')
    }
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 4 }}>Comercial</div>
          <h1 className="me-page-title">Clientes</h1>
        </div>
        <button
          className="me-btn me-btn-primary"
          onClick={() => setCreando(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <PlusIcon size={14} strokeWidth={2} /> Nuevo cliente
        </button>
      </div>

      {/* KPIs rápidos */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total', value: clientes.length, sub: 'clientes activos' },
          { label: 'Mayoristas', value: mayoristas, sub: 'cuentas mayor', accent: true },
          { label: 'Con deuda', value: conDeuda, sub: 'saldo pendiente', warn: conDeuda > 0 },
        ].map(k => (
          <div key={k.label} className="me-card" style={{ flex: 1, padding: '14px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--me-text-3)', marginBottom: 4 }}>
              {k.label}
            </div>
            <div style={{
              fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
              fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1,
              color: k.warn ? 'var(--me-bad)' : k.accent ? 'var(--me-mayor)' : 'var(--me-text)',
            }}>
              {k.value}
            </div>
            <div style={{ fontSize: 11, color: 'var(--me-text-3)', marginTop: 4 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 340 }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--me-text-3)', display: 'flex', pointerEvents: 'none' }}>
            <SearchIcon size={14} strokeWidth={1.8} />
          </span>
          <input
            className="me-input"
            style={{ width: '100%', paddingLeft: 34 }}
            placeholder="Buscar cliente…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>

        {/* Tipo filter */}
        <div style={{ display: 'flex', gap: 6 }}>
          {(['all', 'minorista', 'mayorista'] as const).map(t => (
            <button
              key={t}
              onClick={() => setFiltroTipo(t)}
              style={{
                padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: filtroTipo === t ? 'var(--me-text)' : 'var(--me-surface)',
                color: filtroTipo === t ? 'var(--me-bg)' : 'var(--me-text-2)',
                fontFamily: 'var(--me-sans)', fontSize: 13, fontWeight: filtroTipo === t ? 600 : 400,
                transition: 'background var(--me-dur-short), color var(--me-dur-short)',
              }}
            >
              {t === 'all' ? 'Todos' : t === 'minorista' ? 'Minorista' : 'Mayorista'}
            </button>
          ))}
        </div>

        <button
          onClick={() => setSoloActivos(v => !v)}
          style={{
            padding: '6px 12px', borderRadius: 8, border: '1px solid var(--me-line)',
            background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--me-text-3)',
            fontFamily: 'var(--me-sans)', transition: 'color var(--me-dur-short)',
          }}
        >
          {soloActivos ? 'Ver inactivos' : 'Solo activos'}
        </button>
      </div>

      {/* Tabla */}
      <div className="me-card" style={{ padding: 0, overflow: 'clip' }}>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--me-text-3)' }}>Cargando…</div>
        ) : clientes.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--me-text-3)', fontSize: 13.5 }}>
            {busqueda
              ? `Sin clientes que coincidan con "${busqueda}"`
              : 'Sin clientes registrados — creá el primero con el botón de arriba'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="me-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Teléfono</th>
                  <th style={{ textAlign: 'right' }}>Límite CC</th>
                  <th style={{ textAlign: 'right' }}>Saldo deuda</th>
                  <th style={{ textAlign: 'right', width: 96 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((c, i) => (
                  <tr
                    key={c.id}
                    style={{ background: i % 2 === 0 ? 'transparent' : 'var(--me-surface-2)', opacity: c.activo ? 1 : 0.5 }}
                  >
                    <td style={{ fontWeight: 500 }}>{c.nombre}</td>
                    <td>
                      {c.tipo === 'mayorista'
                        ? <span className="me-pill me-pill-mayor">Mayorista</span>
                        : <span className="me-pill me-pill-neutral">Minorista</span>}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--me-text-2)' }}>
                      {c.telefono ?? <span style={{ color: 'var(--me-text-4)' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13 }}>
                      {c.limite_credito > 0 ? `$${fmt(c.limite_credito)}` : <span style={{ color: 'var(--me-text-4)' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {c.saldo_actual > 0 ? (
                        <button
                          onClick={() => navigate('/clientes/deudas', { state: { clienteId: c.id } })}
                          style={{
                            fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13,
                            fontWeight: 700, color: 'var(--me-bad)', background: 'var(--me-bad-soft)',
                            border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
                            transition: 'opacity var(--me-dur-short)',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
                          onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                        >
                          ${fmt(c.saldo_actual)}
                        </button>
                      ) : (
                        <span style={{ fontFamily: 'var(--me-mono)', fontSize: 13, color: 'var(--me-ok)' }}>$0,00</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button className="me-icon-btn" title="Editar" onClick={() => setEditando(c)}>
                          <EditIcon size={14} strokeWidth={1.8} />
                        </button>
                        <button
                          className="me-icon-btn"
                          title={c.activo ? 'Desactivar' : 'Activar'}
                          onClick={() => handleToggle(c)}
                          style={{ color: c.activo ? 'var(--me-text-3)' : 'var(--me-ok)' }}
                        >
                          {c.activo ? <EyeOffIcon size={14} strokeWidth={1.8} /> : <EyeIcon size={14} strokeWidth={1.8} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(creando || editando) && (
        <ClienteDrawer
          cliente={editando}
          onClose={() => { setCreando(false); setEditando(null) }}
        />
      )}
    </div>
  )
}

// ─── Drawer crear / editar ─────────────────────────────────────────────────────

function ClienteDrawer({ cliente, onClose }: { cliente: Cliente | null; onClose: () => void }) {
  const crear  = useCrearCliente()
  const editar = useEditarCliente()
  const esNuevo = !cliente

  const [form, setForm] = useState({
    nombre:        cliente?.nombre ?? '',
    tipo:          cliente?.tipo ?? 'minorista' as 'minorista' | 'mayorista',
    telefono:      cliente?.telefono ?? '',
    email:         cliente?.email ?? '',
    direccion:     cliente?.direccion ?? '',
    limiteCredito: cliente?.limite_credito ?? 0,
  })

  function setF<K extends keyof typeof form>(k: K, v: typeof form[K]) {
    setForm(f => ({ ...f, [k]: v }))
  }

  async function handleSubmit() {
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return }
    try {
      if (esNuevo) {
        await crear.mutateAsync(form)
        toast.success('Cliente creado')
      } else {
        await editar.mutateAsync({ ...form, id: cliente.id })
        toast.success('Cliente actualizado')
      }
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error')
    }
  }

  const isPending = crear.isPending || editar.isPending

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 38, background: 'rgba(0,0,0,0.28)' }} />
      <div className="me-drawer-in" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 45,
        width: 460, background: 'var(--me-surface)',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.14)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--me-line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div className="me-eyebrow" style={{ marginBottom: 2 }}>Comercial</div>
            <h2 className="me-section-title" style={{ margin: 0 }}>
              {esNuevo ? 'Nuevo cliente' : 'Editar cliente'}
            </h2>
          </div>
          <button className="me-icon-btn" onClick={onClose}><CloseIcon size={18} /></button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Nombre */}
          <Field label="Nombre">
            <input
              className="me-input" style={{ width: '100%' }}
              value={form.nombre}
              onChange={e => setF('nombre', e.target.value)}
              placeholder="Nombre completo o razón social"
              autoFocus={esNuevo}
            />
          </Field>

          {/* Tipo */}
          <Field label="Tipo de cliente">
            <div style={{ display: 'flex', gap: 8 }}>
              {(['minorista', 'mayorista'] as const).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setF('tipo', t)}
                  style={{
                    flex: 1, padding: '9px 12px', borderRadius: 10, border: '2px solid',
                    borderColor: form.tipo === t
                      ? (t === 'mayorista' ? 'var(--me-mayor)' : 'var(--me-text)')
                      : 'var(--me-line)',
                    background: form.tipo === t
                      ? (t === 'mayorista' ? 'var(--me-mayor-soft)' : 'var(--me-surface-2)')
                      : 'none',
                    cursor: 'pointer', fontFamily: 'var(--me-sans)',
                    fontSize: 13, fontWeight: 600,
                    color: form.tipo === t
                      ? (t === 'mayorista' ? 'var(--me-mayor)' : 'var(--me-text)')
                      : 'var(--me-text-3)',
                    transition: 'all var(--me-dur-short)',
                  }}
                >
                  {t === 'minorista' ? 'Minorista' : 'Mayorista'}
                </button>
              ))}
            </div>
          </Field>

          {/* Contacto */}
          <Field label="Teléfono" optional>
            <input
              className="me-input" style={{ width: '100%' }}
              value={form.telefono}
              onChange={e => setF('telefono', e.target.value)}
              placeholder="11 2345-6789"
            />
          </Field>

          <Field label="Email" optional>
            <input
              className="me-input" style={{ width: '100%' }}
              type="email"
              value={form.email}
              onChange={e => setF('email', e.target.value)}
              placeholder="cliente@email.com"
            />
          </Field>

          <Field label="Dirección" optional>
            <input
              className="me-input" style={{ width: '100%' }}
              value={form.direccion}
              onChange={e => setF('direccion', e.target.value)}
              placeholder="Calle, número, localidad"
            />
          </Field>

          {/* Límite de crédito */}
          <Field label="Límite de cuenta corriente" optional>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--me-text-3)', fontSize: 13, pointerEvents: 'none' }}>$</span>
              <input
                className="me-input"
                style={{ width: '100%', paddingLeft: 22, fontFamily: 'var(--me-mono)' }}
                type="number" min={0} step={100}
                value={form.limiteCredito === 0 ? '' : form.limiteCredito}
                onChange={e => setF('limiteCredito', parseFloat(e.target.value) || 0)}
                placeholder="0,00"
              />
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--me-text-4)' }}>
              0 = sin límite definido
            </p>
          </Field>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px 18px', borderTop: '1px solid var(--me-line)', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="me-btn me-btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancelar</button>
            <button
              className="me-btn me-btn-primary"
              onClick={handleSubmit}
              disabled={isPending}
              style={{ flex: 2 }}
            >
              {isPending ? 'Guardando…' : esNuevo ? 'Crear cliente' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Field({ label, optional, children }: { label: string; optional?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--me-text-3)', marginBottom: 6, display: 'flex', gap: 6 }}>
        {label}
        {optional && <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--me-text-4)' }}>(opcional)</span>}
      </div>
      {children}
    </div>
  )
}
